import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import FilterBar from '../../components/FilterBar';
import DateTimePicker from '../../components/DateTimePicker';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import schedulingService from '../../services/scheduling.service';
import api from '../../services/api';
import { config } from '../../config/config';

interface RecodedServiceFailure {
  reschedule_recno: number;
  pronumber: number;
  servicefailuredate: string | null;
  current_reason: string;
  first_reason: string;
  recoded_by: string;
  reviewedby: string;
  revieweddatetime: string | null;
  servicefailuretype: string;
  coded_by: string;
  createdatetime: string | null;
  comments: string;
  lc: string;
  container: string;
  customer: string;
}

const SETTINGS_KEY = 'recoded-service-failures';

/** YYYY-MM-DD in local time (the format the report endpoint expects). */
const formatDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** A DATE column — rendered without a timezone shift. */
const formatDate = (value: string | null): string => {
  if (!value) return '-';
  const [y, m, d] = value.slice(0, 10).split('-');
  return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
};

const formatDateTime = (value: string | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

function RecodedServiceFailuresContent() {
  // Any orders-scope registry field can be added as a column; the backend attaches it
  // to these rows keyed on pronumber. Several rows can share a pro (a pro can have
  // more than one service failure), so an added order column repeats down them.
  const fieldSelection = useAdditionalFieldSelection(SETTINGS_KEY);
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const fieldsReady = fieldSelection.ready;

  const [data, setData] = useState<RecodedServiceFailure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // The range is on the SERVICE FAILURE date, not the create date — a failure coded
  // days later still belongs to the day it happened.
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => new Date());
  const [lcFilter, setLcFilter] = useState<string[]>([]);

  const [lcOptions, setLcOptions] = useState<MultiSelectOption[]>([]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    schedulingService
      .getFilterOptions()
      .then((res) => {
        if (cancelled) return;
        setLcOptions(res.logistics_coordinators.map((lc) => ({ value: lc.value, label: lc.label })));
      })
      .catch(() => { /* leave empty */ });
    return () => { cancelled = true; };
  }, []);

  const fetchData = useCallback(async () => {
    if (!isMounted.current) return;
    if (!startDate || !endDate) return;
    // The saved column selection is part of the request; going early just fetches twice.
    if (!fieldsReady) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setError(null);
      setLoading(true);

      const params: Record<string, string> = {
        start_date: formatDateKey(startDate),
        end_date: formatDateKey(endDate),
      };
      if (lcFilter.length > 0) params.csr = lcFilter.join(',');
      if (additionalFieldsKey) params.additional_fields = additionalFieldsKey;

      const response = await api.get('/reporting/recoded-service-failures', {
        params,
        signal: controller.signal,
      });
      if (!isMounted.current || controller.signal.aborted) return;
      setData(response.data);
    } catch (err) {
      if (!isMounted.current || controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      // Only the request still in flight owns the spinner — an aborted fetch must not
      // switch it off while its replacement is still loading.
      if (isMounted.current && abortControllerRef.current === controller) setLoading(false);
    }
  }, [startDate, endDate, lcFilter, fieldsReady, additionalFieldsKey]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  const baseColumns = useMemo<ColumnDef<RecodedServiceFailure>[]>(() => [
    {
      accessorKey: 'pronumber',
      header: 'PRO #',
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const pronumber = row.original.pronumber;
        return (
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); openOrderDrawer(Number(pronumber)); }}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              {pronumber}
            </button>
            <a
              href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { e.stopPropagation(); }}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              title="Open in CMSV1"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: 'servicefailuredate',
      header: 'SF Date',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDate(row.original.servicefailuredate),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'customer',
      header: 'Customer',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'lc',
      header: 'LC',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'first_reason',
      header: 'Original Reason',
      filterFn: 'arrIncludes',
      // Blank means no reason history exists for this service failure at all — see the
      // note under the filter bar. It is NOT the same as "was never changed".
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'coded_by',
      header: 'Coded By',
      filterFn: 'arrIncludes',
      // Who coded the ORIGINAL reason, from the first history row — not
      // reschedule.coded_by, which re-stamps on every change and would name the
      // re-coder. Blank when V1 did the original coding and logged nobody.
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'current_reason',
      header: 'Current Reason',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'recoded_by',
      header: 'Recoded By',
      filterFn: 'arrIncludes',
      // Blank on a recoded row means the change was made in V1, which logs nothing.
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'reviewedby',
      header: 'Reviewed By',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'revieweddatetime',
      header: 'Reviewed Date',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDateTime(row.original.revieweddatetime),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'servicefailuretype',
      header: 'SF Type',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'container',
      header: 'Container',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'createdatetime',
      header: 'SF Created',
      // When the SERVICE FAILURE was created, not the order's `createdatetime` its id
      // would otherwise be titled from.
      meta: { registryTitle: false },
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDateTime(row.original.createdatetime),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'comments',
      header: 'Comments',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
  ], [openOrderDrawer]);

  const { columns, availableFields } = useAdditionalFieldColumns<RecodedServiceFailure>(
    fieldSelection, { baseColumns },
  );

  const getRowId = useCallback((row: RecodedServiceFailure) => String(row.reschedule_recno), []);

  const filterBar = (
    <FilterBar label="">
      <div className="flex items-center gap-1">
        <span>Service failure between</span>
        <DateTimePicker
          mode="date"
          selectionType="single"
          value={startDate}
          onChange={(val) => setStartDate((val as Date | null) ?? null)}
          inputVariant="compact"
          labelType="none"
          placeholder="Start"
          autoApply
        />
        <span>and</span>
        <DateTimePicker
          mode="date"
          selectionType="single"
          value={endDate}
          onChange={(val) => setEndDate((val as Date | null) ?? null)}
          inputVariant="compact"
          labelType="none"
          placeholder="End"
          autoApply
        />
      </div>
      <FilterBar.Divider />
      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">LC</label>
      <MultiSelect
        options={lcOptions}
        value={lcFilter}
        onChange={setLcFilter}
        placeholder="All LCs"
        showClearOption
        clearOptionLabel="All LCs"
        enableSearch
        searchPlaceholder="Search LCs..."
        size="sm"
        className="min-w-[200px]"
      />
    </FilterBar>
  );

  if (error) {
    return (
      <div className="flex flex-col h-full">
        {filterBar}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {filterBar}

      <div className="px-4 py-2 text-sm text-muted bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        Service failures whose reason no longer matches the first one on record.
        {' '}
        <strong>This is an undercount.</strong> Only 360 records a service failure&rsquo;s
        original reason, and only since 5/22/26 — a re-code on a row 360 never touched
        leaves no trace, so it cannot appear here. Everything listed is a real re-code;
        the ones missing are the ones with no evidence either way. A blank Recoded By
        means the change itself was made in V1, which logs nothing; a blank Coded By
        means the same of the original coding.
      </div>

      <div className="flex-1 min-h-0 p-4">
        <TanTable
          data={data}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          trackViewedColumnId="pronumber"
          loading={loading}
          defaultSorting={[{ id: 'servicefailuredate', desc: true }]}
          settingsKey={SETTINGS_KEY}
          enableColumnFilters={true}
          enableGrouping={true}
          getRowId={getRowId}
          cardTitle="Recoded Service Failures"
          fillContainer={true}
        />
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}

export default function RecodedServiceFailures() {
  return (
    <ViewedProsProvider>
      <RecodedServiceFailuresContent />
    </ViewedProsProvider>
  );
}
